// Device controls on the ledger.
//
// These issue REAL commands to a REAL screen in a REAL cab that may have a
// passenger in it right now. Three consequences shape this component:
//
// 1 · IT INVENTS NOTHING. Every command, its parameter bounds and its risk
//     level come from server/data/ggosCommandDefinitions.json via
//     POST /api/ggos-devices/:thingName/commands — the same path the classic
//     Command Panel uses, and the same one that writes ggos_command_audit_log.
//     A second command vocabulary would be a second source of truth about
//     what a device can be told to do.
//
// 2 · CONFIRMATION IS INFORMED, NOT RITUAL. The server only forces `confirmed`
//     on CRITICAL commands, and none of these are. But rebooting or sleeping a
//     screen mid-ride interrupts an impression somebody is being charged for,
//     and THIS page already knows whether a passenger is aboard and whether
//     the cab is moving. So the dialog says so — "a passenger is aboard right
//     now" — instead of asking "are you sure?" about nothing in particular.
//     A confirmation that carries no information trains people to click past
//     it.
//
// 3 · THE RESULT LANDS IN THE LEDGER. Every command writes to
//     ggos_command_audit_log, which is the Operations lane a few pixels below.
//     After one is issued the day is refetched, so the action appears in the
//     same timeline as its consequences, attributed to whoever issued it.

import React, { useCallback, useState } from 'react';
import { Sun, Volume2, Moon, RotateCw, Gauge, PackageSearch, X, AlertTriangle } from 'lucide-react';
import api from '../../../services/api';
import s from './Commands.module.css';

// Mirrors ggosCommandDefinitions.json. Bounds are repeated here ONLY to keep
// the input from offering a value the server will reject; the server remains
// the authority and rejects anything out of range regardless.
const CONTROLS = [
  { op: 'set_brightness', label: 'Brightness', icon: Sun, risk: 'SAFE',
    kind: 'range', min: 0, max: 255, step: 5,
    fromState: (st) => (st?.brightness_percent != null ? Math.round(st.brightness_percent * 255 / 100) : 128),
    display: (v) => `${Math.round((v / 255) * 100)}%`,
    params: (v) => ({ level: v }),
    blurb: 'Screen brightness, 0–255. The device reports it back as a percentage.' },

  { op: 'set_volume', label: 'Volume', icon: Volume2, risk: 'SAFE',
    kind: 'range', min: 0, max: 15, step: 1,
    fromState: (st) => (st?.volume_percent != null ? Math.round(st.volume_percent * 15 / 100) : 0),
    display: (v) => `${v}/15`,
    params: (v) => ({ stream: 'media', level: v }),
    blurb: 'Media volume, 0–15. In-cab screens run muted by design — raising this is a deliberate act.' },

  { op: 'set_power_mode', label: 'Sleep / Wake', icon: Moon, risk: 'MEDIUM',
    kind: 'toggle',
    options: [{ v: true, label: 'Sleep' }, { v: false, label: 'Wake' }],
    params: (v) => ({ low_power: v }),
    confirmWhen: (v) => v === true,
    blurb: 'Sleep turns the screen off, pauses sensing and stops ads. Reversible, and works on every SKU.' },

  { op: 'run_speed_test', label: 'Speed test', icon: Gauge, risk: 'MEDIUM',
    kind: 'action', confirm: true,
    params: () => ({ size_kb: 512 }),
    blurb: 'Downloads 512 KB over the cab’s mobile data to measure real throughput. Metered — run on demand, not on a schedule.' },

  { op: 'reboot', label: 'Reboot', icon: RotateCw, risk: 'MEDIUM',
    kind: 'action', confirm: true, destructive: true,
    params: () => ({}),
    blurb: 'Restarts the device. It will be off air for a minute or two and will play nothing while it boots.' },

  { op: 'get_installed_packages', label: 'Refresh packages', icon: PackageSearch, risk: 'SAFE',
    kind: 'action',
    params: () => ({}),
    blurb: 'Asks the device for its installed app list. Read-only; nothing changes on the screen.' },
];

export default function Commands({ thingName, state, onIssued }) {
  const [pending, setPending] = useState(null);      // op currently in flight
  const [sent, setSent] = useState({});              // op -> { id, at, error }
  const [confirm, setConfirm] = useState(null);      // { control, value }
  const [values, setValues] = useState(() => {
    const v = {};
    for (const c of CONTROLS) if (c.fromState) v[c.op] = c.fromState(state);
    return v;
  });

  const issue = useCallback(async (control, value) => {
    setPending(control.op);
    setConfirm(null);
    try {
      const r = await api.post(
        `/ggos-devices/${encodeURIComponent(thingName)}/commands`,
        { operation: control.op, parameters: control.params(value), confirmed: true });
      setSent((p) => ({ ...p, [control.op]: { id: r.data?.data?.command_id, at: Date.now() } }));
      // The command is now a row in ggos_command_audit_log — refetch so it
      // appears in the Operations lane with everything it caused.
      if (onIssued) setTimeout(onIssued, 1200);
    } catch (e) {
      setSent((p) => ({ ...p, [control.op]: {
        error: e?.response?.data?.error || e?.message || 'Command failed' } }));
    } finally {
      setPending(null);
    }
  }, [thingName, onIssued]);

  // EVERY control confirms. The server only forces it on CRITICAL, and the
  // first cut confirmed only the disruptive ones — but brightness and volume
  // are just as visible to a passenger mid-ride as a reboot is, and a slider
  // is the easiest control in the world to nudge by accident. Founder's call,
  // and it also makes the pending state below meaningful: nothing leaves this
  // page without a second, informed press.
  const request = (control, value) => setConfirm({ control, value });

  const offline = state && state.connection_status !== 'online';

  return (
    <div className={s.wrap}>
      <div className={s.head}>
        <span className={s.title}>Controls</span>
        {/*
          This said "commands will queue, not land". Both halves were false and
          in the reassuring direction. The agent connects withCleanSession(true)
          (MqttConnectionManager.kt:142), so AWS IoT keeps NOTHING for a
          subscriber that is not there — the broker drops the message and no
          QoS changes that. 97 of the 181 historically stuck command rows were
          issued to an offline device and none of them ever reported.
        */}
        {offline && (
          <span className={s.offline}>
            <AlertTriangle size={12} aria-hidden="true" />
            device is {state.connection_status || 'not connected'} — a command sent now is discarded by the broker, not queued
          </span>
        )}
      </div>

      <div className={s.grid}>
        {CONTROLS.map((c) => {
          const Icon = c.icon;
          const busy = pending === c.op;
          const done = sent[c.op];
          return (
            <div key={c.op} className={s.card} title={c.blurb}>
              <div className={s.cardHead}>
                <Icon size={13} aria-hidden="true" />
                <span className={s.cardLabel}>{c.label}</span>
                {c.risk !== 'SAFE' && <span className={s.risk}>{c.risk}</span>}
              </div>

              {c.kind === 'range' && (() => {
                // PENDING = the slider no longer matches what the device last
                // reported. Dragging changes nothing on the cab; it stages a
                // value. The row says so, shows the move (44% -> 70%), and the
                // only way out is Send or Reset — so a stray drag can never be
                // mistaken for a change that landed.
                const live = c.fromState(state);
                const val = values[c.op] ?? live;
                const dirty = val !== live;
                return (
                  <div className={`${s.rangeRow} ${dirty ? s.pendingRow : ''}`}>
                    <input
                      type="range" className={s.range}
                      min={c.min} max={c.max} step={c.step}
                      value={val}
                      onChange={(e) => setValues((v) => ({ ...v, [c.op]: Number(e.target.value) }))}
                      aria-label={c.label}
                      disabled={busy}
                    />
                    <span className={`${s.rangeVal} ${dirty ? s.pendingVal : ''}`}>
                      {dirty
                        ? <><span className={s.was}>{c.display(live)}</span>&nbsp;→&nbsp;{c.display(val)}</>
                        : c.display(val)}
                    </span>
                    {dirty && (
                      <button type="button" className={s.reset} disabled={busy}
                              title="Discard this change and return to what the device reports"
                              onClick={() => setValues((v) => ({ ...v, [c.op]: live }))}>
                        Reset
                      </button>
                    )}
                    <button type="button"
                            className={`${s.apply} ${dirty ? s.primary : ''}`}
                            disabled={busy || !dirty}
                            title={dirty ? 'Confirm and send to the device'
                                         : 'Drag the slider to stage a change'}
                            onClick={() => request(c, val)}>
                      {busy ? '…' : dirty ? 'Send' : 'Set'}
                    </button>
                  </div>
                );
              })()}

              {c.kind === 'toggle' && (
                <div className={s.toggleRow}>
                  {c.options.map((o) => (
                    <button key={String(o.v)} type="button" className={s.apply} disabled={busy}
                            onClick={() => request(c, o.v)}>
                      {busy ? '…' : o.label}
                    </button>
                  ))}
                </div>
              )}

              {c.kind === 'action' && (
                <button type="button"
                        className={`${s.apply} ${c.destructive ? s.destructive : ''}`}
                        disabled={busy} onClick={() => request(c, null)}>
                  {busy ? 'Sending…' : 'Run'}
                </button>
              )}

              {c.kind === 'range' && (values[c.op] ?? c.fromState(state)) !== c.fromState(state) && (
                <span className={s.pendingNote}>pending — not sent yet</span>
              )}
              {done?.error && <span className={s.err}>{done.error}</span>}
              {done?.id && !done.error && <span className={s.ok}>sent · appears in Operations</span>}
            </div>
          );
        })}
      </div>

      {confirm && (
        <ConfirmDialog
          control={confirm.control}
          value={confirm.value}
          state={state}
          onCancel={() => setConfirm(null)}
          onConfirm={() => issue(confirm.control, confirm.value)}
        />
      )}
    </div>
  );
}

// The dialog's job is to say what this will INTERRUPT, using what the page
// already knows. "Are you sure?" with no facts in it is a click-through.
function ConfirmDialog({ control, value, state, onCancel, onConfirm }) {
  // Only what the blurb does NOT already say, and only what is true right
  // now. A dialog that repeats itself reads as boilerplate.
  const moving = control.kind === 'range'
    ? `${control.display(control.fromState(state))} → ${control.display(value)}`
    : null;
  const consequences = [
    moving && `The screen changes from ${moving} immediately.`,
    state?.passenger_aboard && 'A passenger is aboard right now — this will interrupt an impression that is being charged for.',
    state?.is_moving && 'The cab is moving.',
    control.op === 'set_power_mode' && value === true && 'Sensing pauses and ads stop until the device is woken again.',
    control.op === 'run_speed_test' && 'This uses the cab’s metered mobile data.',
  ].filter(Boolean);

  return (
    <div className={s.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className={s.dialog} role="dialog" aria-modal="true" aria-label={`Confirm ${control.label}`}>
        <div className={s.dialogHead}>
          <span className={s.dialogTitle}>{control.label}</span>
          <button type="button" className={s.close} onClick={onCancel} aria-label="Cancel">
            <X size={15} />
          </button>
        </div>
        {moving && <p className={s.dialogMove}>{moving}</p>}
        <p className={s.dialogBody}>{control.blurb}</p>
        {consequences.length > 0 && (
          <ul className={s.consequences}>
            {consequences.map((c) => <li key={c}>{c}</li>)}
          </ul>
        )}
        <div className={s.dialogActions}>
          <button type="button" className={s.apply} onClick={onCancel}>Cancel</button>
          <button type="button" className={`${s.apply} ${control.destructive ? s.destructive : s.primary}`}
                  onClick={onConfirm}>
            {control.op === 'reboot' ? 'Reboot anyway' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
